import { useEffect, useState } from 'react';
import { agentPlatformAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * Whether the signed-in user may use the Agent Builder: Admins always, users
 * only when an admin granted it. `null` while loading. Cached per user like
 * useConnectorAccess; call {@link invalidateAgentBuilderAccess} after a change
 * that affects it. A failed load counts as "no access" and is not cached.
 */
let cache: { userId: number; promise: Promise<boolean> } | null = null;

export const invalidateAgentBuilderAccess = () => {
  cache = null;
};

export const useAgentBuilderAccess = (): boolean | null => {
  const { user } = useAuth();
  const [enabled, setEnabled] = useState<boolean | null>(null);

  useEffect(() => {
    if (!user) {
      setEnabled(null);
      return;
    }
    if (!cache || cache.userId !== user.userId) {
      const request = agentPlatformAPI.access().then(r => Boolean(r.data.enabled));
      const entry = { userId: user.userId, promise: request.catch(() => false) };
      cache = entry;
      // Drop a failed load so the next mount asks again.
      request.catch(() => { if (cache === entry) cache = null; });
    }
    let alive = true;
    cache.promise.then(a => { if (alive) setEnabled(a); });
    return () => { alive = false; };
  }, [user]);

  return enabled;
};
